import { Component, DestroyRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { of } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap } from 'rxjs';

import * as conditionGroups from '@shared-constants/condition-groups';
import { COST_CENTER_PATTERN } from '@shared-constants/pattern-constants';
import { TimesheetTypeEnum } from '@shared-enums/cats.enum';
import { arrayIsTruthy, findUnique } from '@shared-helpers/array-object-helpers';
import { convertSapDateToMoment } from '@shared-helpers/date-helpers';
import { safeCompare, trimLeadingZeros } from '@shared-helpers/string-number-helpers';
import { IHttpResponseCostCenter, ISapResponseContractCondition, IWebServerResponseApprover } from '@shared-interfaces/api';

import { IModalContentCreateOrEditTimesheetResult } from '../../components/modal/modal-content-timesheet-create-or-edit/modal-content-timesheet-create-or-edit.component';
import { ITableLikeLayoutEvent } from '../../components/table-like-layout/table-like-layout.component';
import { TabsComponent } from '../../components/tabs/tabs.component';
import { ITabsConfig } from '../../components/tabs/tabs.model';
import { ISelectOptions, ITableConfigEntity } from '../../interfaces/internal-interfaces';
import { ConditionHandler } from '../../scripts/ConditionHandler';
import { ContractHandler } from '../../scripts/contracts/contract-handler';
import { ExtDocument } from '../../scripts/extDocument';
import { makeComplexConfigItem } from '../../scripts/table-configs/table-config-helpers';
import { makeTimesheetManagerConfig } from '../../scripts/table-configs/timesheet-manager-tables.config';
import { ApiService } from '../../services/api.service';
import { DatahubService } from '../../services/datahub.service';
import { MessageService } from '../../services/message.service';
import { ModalService } from '../../services/modal.service';
import { TimesheetHelperService } from '../../services/timesheet-helper.service';
import { ICostCenterConditionDisplay, INonbillTabData } from './timesheet-manager.model';

@Component({
  selector: 'timesheet-manager',
  templateUrl: './timesheet-manager.component.html',
  styleUrl: './timesheet-manager.component.css',
  standalone: false,
})
export class TimesheetManagerComponent implements OnInit, OnDestroy {
  @ViewChild(TabsComponent) public readonly costCenterTabs!: TabsComponent<ContractHandler | INonbillTabData>;

  public config: ITableConfigEntity<keyof ExtDocument>[] = [];
  public isSearchingForCostCenter = true;
  public handler = this.datahub.weDate;
  public costCenters: string[] = structuredClone(this.datahub.user.costCentersForApproval);
  public timesheets: ExtDocument[] = [];
  public conditionsInSearch: Array<ContractHandler | INonbillTabData> = [];
  public customerGrpsInSearch: string[] = [];
  public currentCustomerGrpIndex: number;
  public shouldDisableAddTimesheet = false;
  public isRetrievingCostCenterName = false;
  public retrievedCostCenterInfo?: IHttpResponseCostCenter;
  public isCreatingTimesheet = false;
  public horizontalTabsConfig: ITabsConfig<ContractHandler | INonbillTabData>[] = [];
  public doesContainAnyContractsExpiringSoon = false;
  public readonly costCenterFormCtrl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.pattern(COST_CENTER_PATTERN)],
  });

  private contracts: ContractHandler[] = [];
  private currentCondition: string;
  private currentCostcenter: string;
  private currentCustomerGrp: string;

  constructor(
    private readonly datahub: DatahubService,
    private readonly apiService: ApiService,
    private readonly messageService: MessageService,
    private readonly timesheetHelperSvc: TimesheetHelperService,
    private readonly modalSvc: ModalService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly destroyRef: DestroyRef,
  ) {}

  public ngOnInit(): void {
    this.datahub.switchApprover$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (x) => {
        this.switchForeman(x);
      },
      error: (err) => {
        console.error('Error switching approver:', err);
      },
    });

    this.costCenterFormCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        if (this.costCenterFormCtrl.valid) {
          //Input is valid, now we see if it really exists or not
          this.costCenterFormCtrl.markAllAsTouched(); //trigger validation
          this.isRetrievingCostCenterName = true;
          this.retrievedCostCenterInfo = undefined;
          this.fetchCostCenter();
        }
      },
      error: (err) => {
        console.error('Error during cost center form value change:', err);
      },
    });

    this.activatedRoute.queryParams
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((params: Params) => {
          this.handler.setWeDateWithString(params['wedate'], false);
          const approver = params['approver'] || this.datahub.user.getApprover();
          if (safeCompare(approver, this.datahub.user.approvingId)) {
            this.search();
            return of(null);
          } else {
            return this.datahub.user.switchApprover$(approver).pipe(
              switchMap((x) => {
                this.switchForeman(x);
                return of(null);
              }),
            );
          }
        }),
      )
      .subscribe({
        error: (err) => {
          console.error('Error handling queryParams or switching approver:', err);
        },
      });
  }

  public getHorizontalTabConfigs(horizontalTabsConfig: ITabsConfig<ContractHandler | INonbillTabData>[]): void {
    this.horizontalTabsConfig = horizontalTabsConfig;
  }

  public ngOnDestroy(): void {
    this.timesheets.forEach((doc: ExtDocument) => {
      doc.killRefreshTimer();
    });
  }

  private switchForeman(approver: IWebServerResponseApprover): void {
    this.costCenters = structuredClone(approver.costCenters);
    this.search();
  }

  public resetCostCenterValidationIfEmpty(): void {
    if (this.costCenterFormCtrl.value == null) {
      this.costCenterFormCtrl.reset(); //Reset value & validation again to prevent error from showing as the field is blurred
      this.retrievedCostCenterInfo = undefined;
    }
  }

  public fetchCostCenter(): void {
    this.apiService.getCostCenter$(this.costCenterFormCtrl.value.toString()).subscribe({
      next: (cc) => {
        this.isRetrievingCostCenterName = false;
        if (cc == null) this.costCenterFormCtrl.setErrors({ costCenterDoesNotExist: true });
        this.retrievedCostCenterInfo = cc;
      },
      error: (err) => {
        this.isRetrievingCostCenterName = false;
        console.error('Error retrieving cost center:', err);
      },
    });
  }

  public addCostCenterTab(): void {
    if (this.costCenterFormCtrl.valid && this.retrievedCostCenterInfo != null) {
      if (this.costCenters.includes(this.retrievedCostCenterInfo.CostCtr)) {
        //CC has already been added, we just switch it to it
        const selectedTab = this.horizontalTabsConfig.find((tab) => tab.label2 === this.retrievedCostCenterInfo?.CostCtr);
        if (selectedTab != null) this.costCenterTabs.setSelectedTab(selectedTab, this.horizontalTabsConfig);
      } else {
        this.costCenters.push(this.retrievedCostCenterInfo.CostCtr);
        this.search();
      }

      //Reset values & validation
      this.costCenterFormCtrl.reset();
      this.retrievedCostCenterInfo = undefined;
    }
  }

  public removeCostCenterTab(ccToRemove: string): void {
    this.costCenters = this.costCenters.filter((cc) => cc !== ccToRemove);
    this.search();
  }

  private search(): void {
    this.datahub.contracts.ready$
      .pipe(
        filter((isReady) => !!isReady),
        switchMap(() => {
          this.timesheets = [];
          this.conditionsInSearch = [];
          this.isSearchingForCostCenter = true;

          if (this.costCenters.length === 0) {
            this.costCenters = [this.datahub.user.getHomeCC()];
          }

          return this.datahub.contracts.complexSearchByCostCenter$(this.costCenters, this.handler);
        }),
        concatMap((contracts: ContractHandler[]) => {
          this.contracts = contracts;
          const uniqueContracts = this.getUniqueContracts(this.contracts);

          let timesheetDisplayData: ICostCenterConditionDisplay[] = uniqueContracts.map((contract) => ({
            costcenter: this.isStormOrPrivate(contract.value.CondGroup) ? '' : contract.value.CostCenter,
            condGroup: contract.value.CondGroup,
            custGroup: contract.value.CustGroup,
            customerName: contract.value.CustomerName,
          }));

          timesheetDisplayData = this.getUniqueTimesheet(timesheetDisplayData);

          if (!arrayIsTruthy(timesheetDisplayData)) {
            this.conditionsInSearch.push(
              { value: { CustomerName: 'Nonbill', isNonbill: true, shouldHideCloseButton: true } },
              { value: { CustomerName: 'Per Diem', isNonbill: false, shouldHideCloseButton: true } },
            );

            const params = {
              Dateentered: this.handler.getFilterString(),
              Owner: this.datahub.user.approvingId,
            };

            if (this.conditionsInSearch[0]) {
              this.handleTabChange(this.conditionsInSearch[0]);
            }

            return this.apiService.getTimesheets$(params);
          }

          this.conditionsInSearch = timesheetDisplayData
            .map((group) => {
              const recordToChange = uniqueContracts.find((contract) => {
                if (this.isStormOrPrivate(group.condGroup)) {
                  return contract.value.CondGroup === group.condGroup && contract.value.CustGroup === group.custGroup;
                } else {
                  return (
                    contract.value.CondGroup === group.condGroup &&
                    contract.value.CustGroup === group.custGroup &&
                    contract.value.CostCenter === group.costcenter
                  );
                }
              });

              if (recordToChange) {
                if (group.condGroup === conditionGroups.CONDITION_GROUP_SPECIAL_ID_PRIVATE) {
                  recordToChange.value.shouldHideCloseButton = true;
                  recordToChange.value.CustomerName = 'Private \n Work';
                  recordToChange.value.CostCenterDisplay = '';
                } else if (group.condGroup === conditionGroups.CONDITION_GROUP_SPECIAL_ID_STORM) {
                  recordToChange.value.shouldHideCloseButton = true;
                  recordToChange.value.CustomerName = 'Off System \n Storm';
                  recordToChange.value.CostCenterDisplay = '';
                } else if (group.condGroup === conditionGroups.CONDITION_GROUP_SPECIAL_ID_WOLF_STORM) {
                  recordToChange.value.shouldHideCloseButton = true;
                  recordToChange.value.CustomerName = 'Wolf Off Sys \n Storm';
                  recordToChange.value.CostCenterDisplay = '';
                } else {
                  recordToChange.value.shouldHideCloseButton = false;
                  recordToChange.value.CostCenterDisplay = recordToChange.value.CostCenter;
                }
              }

              return recordToChange;
            })
            .filter((g) => g != null);

          this.conditionsInSearch.sort((a) =>
            a instanceof ContractHandler && conditionGroups.CONDITION_GROUP_SPECIAL_DESC_PATTERN.test(a.contractName) ? 1 : -1,
          );

          this.conditionsInSearch.push(
            { value: { CustomerName: 'Nonbill', isNonbill: true, shouldHideCloseButton: true } },
            { value: { CustomerName: 'Per Diem', isNonbill: false, shouldHideCloseButton: true } },
          );

          const params = {
            Dateentered: this.handler.getFilterString(),
          };

          return this.apiService.getTimesheets$(params);
        }),
      )
      .subscribe({
        next: (documents) => {
          this.datahub.updateQueryParams();
          this.isSearchingForCostCenter = false;

          if (arrayIsTruthy(documents)) {
            const raw = documents.filter((document) => {
              if (document.Nonbillable) return safeCompare(document.Owner, this.datahub.user.approvingId);
              if (document.Type === TimesheetTypeEnum.PerDiem) {
                return safeCompare(document.Owner, this.datahub.user.approvingId);
              }
              return this.costCenters.find((x) => document.Costcenter1 === x);
            });
            this.timesheets = raw.map((doc) => new ExtDocument(doc, this.datahub, true));
            this.handleTabChange(this.conditionsInSearch[0] as ContractHandler);
          } else {
            this.timesheets = [];
          }
        },
        error: (err) => {
          this.isSearchingForCostCenter = false;
          console.error('Error searching for timesheets:', err);
        },
      });
  }

  public handleTabChange(condition: string | ContractHandler | INonbillTabData): void {
    if (typeof condition === 'string') {
      //A SIDE CONDITION GROUP TAB was clicked
      this.handleCustomerGrpChange(condition);
    } else if (condition instanceof ContractHandler) {
      //A TOP COST CENTER TAB was clicked, OR this was called form the search function above
      this.shouldDisableAddTimesheet = false;
      this.currentCondition = condition.value.CondGroup;
      this.currentCostcenter = condition.value.CostCenter;
      const contractsInCondition = this.contracts.filter(
        (x) => x.getConditionGrp() === condition.value.CondGroup && x.getCostCenter() === condition.value.CostCenter,
      );
      this.customerGrpsInSearch = findUnique(contractsInCondition, 'value.CustGroup', false);
      if (this.customerGrpsInSearch[0] != null) this.handleCustomerGrpChange(this.customerGrpsInSearch[0]);
    } else if (condition.value.CustomerName === 'Nonbill') {
      this.handleNonbillCase();
    } else {
      this.handlePerDiemCase();
    }
  }

  public willExpireWithinTwoWeeksOfWeDate(contract?: ContractHandler): boolean {
    if (contract == null) return false;
    const contractEndDate = convertSapDateToMoment(contract.value.ValidTo);
    const twoWeeksFromWeDate = this.datahub.weDate.getWeDate().add(2, 'weeks');
    return contractEndDate.isBefore(twoWeeksFromWeDate);
  }

  private handleCustomerGrpChange(custGrp: string): void {
    this.currentCustomerGrpIndex = this.customerGrpsInSearch.findIndex((x) => x === custGrp);
    this.currentCustomerGrp = this.customerGrpsInSearch[this.currentCustomerGrpIndex];
    const found = this.datahub.contracts.getCustomerConditionFromStore(this.currentCondition, custGrp);
    if (!found) throw Error('Could not find custgrp condgrp combo in search cache');
    this.config = this.buildTableFromHeaders(found);
    this.doesContainAnyContractsExpiringSoon = false;

    this.timesheets.forEach((timesheet) => {
      if (this.costCenters.some((costcenter) => timesheet.Costcenter1 === costcenter)) {
        if (this.isStormOrPrivate(this.currentCondition)) {
          timesheet.shouldShow = this.shouldTimesheetShow(timesheet, custGrp, true);
        } else {
          timesheet.shouldShow = this.shouldTimesheetShow(timesheet, custGrp, false);
          //Flag contracts ending soon with a class
          if (this.willExpireWithinTwoWeeksOfWeDate(timesheet.contractDetails)) {
            if (timesheet.shouldShow) this.doesContainAnyContractsExpiringSoon = true;
            //Within the data, find the end date within the string and wrap it with some HTML so we can style it separately
            const matchEndDate = new RegExp(`(${timesheet.contractEndDate})$`, 'i');
            timesheet.contractDisplay = timesheet.contractDisplay.replace(
              matchEndDate,
              '<strong class="text-red-600">$1</strong>',
            );
          }
        }
        timesheet.Documentno = trimLeadingZeros(timesheet.Documentno);
        timesheet.modifyConfig(this.config);
      } else {
        timesheet.shouldShow = false;
      }
    });
  }

  private shouldTimesheetShow(timesheet: ExtDocument, custGrp: string, isStormOrPrivate: boolean): boolean {
    // When private or storm work tab selected, don't check the cost center as we want to get all cost centers
    // Any other case we want to make sure we check customer group, condition group and cost center or some timesheets will show
    // under multiple tabs.
    const isCustomerAndCondition = timesheet.CustGroup === custGrp && timesheet.CondGroup === this.currentCondition;
    if (isStormOrPrivate) return isCustomerAndCondition;
    return timesheet.Costcenter1 === this.currentCostcenter && isCustomerAndCondition;
  }

  private handleNonbillCase(): void {
    this.shouldDisableAddTimesheet = true;
    this.customerGrpsInSearch = [];
    this.currentCustomerGrpIndex = 0;
    this.config = makeTimesheetManagerConfig(true, false, false, this.datahub.approvers.selectOptions);
    this.timesheets.forEach((x) => {
      if (x.isNonBill() && !x.isPerDiem()) {
        x.shouldShow = true;
        x.Documentno = trimLeadingZeros(x.Documentno);
        x.modifyConfig(this.config);
      } else {
        x.shouldShow = false;
      }
    });
  }

  private handlePerDiemCase(): void {
    this.shouldDisableAddTimesheet = true;
    this.customerGrpsInSearch = [];
    this.currentCustomerGrpIndex = 0;
    this.config = makeTimesheetManagerConfig(false, true, false, this.datahub.approvers.selectOptions);
    this.timesheets.forEach((x) => {
      if (x.isPerDiem()) {
        x.shouldShow = true;
        x.Documentno = trimLeadingZeros(x.Documentno);
        x.modifyConfig(this.config);
      } else {
        x.shouldShow = false;
      }
    });
  }

  private buildTableFromHeaders(
    condition: ISapResponseContractCondition | ConditionHandler,
  ): ITableConfigEntity<keyof ExtDocument>[] {
    const headers = condition.CondToTimesheetHdr.results;
    const headersConfig = headers?.map((element) => {
      const options: ISelectOptions[] = (element.TimesheetConfigValuesSet.results ?? []).map((x) => {
        return {
          value: x.Fldval,
          description: x.FldvaldescEnglish,
        };
      });
      options.push({ value: '', description: '' }); //blank item

      // TODO: Uncomment to enable the clickable headers to launch the modal to edit the DDL values
      // const isDropdownHeader = element.Valtype === 'L1'; //apparently "L1" means this is a dropdown!
      return makeComplexConfigItem<ExtDocument>(
        element.Englishlabel,
        `Genericfld${element.TimeGenFld}` as keyof ExtDocument,
        element.Valtype,
        2,
        false,
        false,
        {
          selectOptions: options,
          class: 'pl-2 ml-1',
          headerClass: 'text-left pl-4',
          // tooltip: isDropdownHeader ? 'Click the table header to edit the items in this menu' : undefined,
          // detectHeaderClick: isDropdownHeader,
        },
      );
    });

    if (this.handler.isCurrentWeek()) {
      headersConfig?.push(
        makeComplexConfigItem<ExtDocument>('Renew', 'Autocreate', 'checkbox', 1, false, false, {
          tooltip:
            'If this box is checked, this timesheet will be duplicated next week with the same values and a new timesheet number.',
        }),
      );
    }

    return makeTimesheetManagerConfig(
      false,
      false,
      this.isStormOrPrivate(condition.CondGroup),
      this.datahub.approvers.selectOptions,
    ).concat(headersConfig ?? []);
  }

  public handleValueChanged(event: ITableLikeLayoutEvent<ExtDocument>): void {
    event.entity.updateSelf();
  }

  public handleIconClicked(event: ITableLikeLayoutEvent<ExtDocument>): void {
    const entity = event.entity;
    if (event.columnConfig.clickValue === 'showExtDoc') {
      window.open(`${entity.Link}?web=1`, '_blank');
    } else if (event.columnConfig.clickValue === 'deleteClicked') {
      //Immediately show a spinner to indicate it is working
      const originalType = event.columnConfig.type;
      event.columnConfig.type = 'spinner';

      //Actually call the deletion method
      entity.deleteSelf$().subscribe({
        next: (extDocumentNumber) => {
          //remove it from the list once it completes the deletion
          this.timesheets = this.timesheets.filter((x) => x.Documentno !== extDocumentNumber);
        },
        error: () => {
          //Restore the original type before the deletion (which should be 'icon')
          event.columnConfig.type = originalType;
        },
      });
    }
  }

  public handlePlainButtonClicked(event: ITableLikeLayoutEvent<ExtDocument>): void {
    const currentTabsTimesheets = this.timesheets.filter((timesheet) => timesheet.shouldShow);
    if (event.columnConfig.clickValue === 'timesheetDescriptionEditClicked') {
      this.modalSvc
        .launchResponsiveModal(
          'timesheet-create-or-edit',
          {
            selectedCostCenterCondGrpTabs: {
              costCenter: this.currentCostcenter,
              custGroup: this.currentCustomerGrp,
            },
            allExtDocuments: currentTabsTimesheets,
            existingDescription: event.entity.Description,
          },
          { autoFocus: 'first-tabbable' },
        )
        .afterClosed()
        .pipe(filter((x) => x)) //remove empty, happens when modal is closed without saving
        .subscribe({
          next: (result: IModalContentCreateOrEditTimesheetResult) => {
            event.entity.Description = result.description;
            this.handleValueChanged(event);
          },
          error: (err) => {
            console.error('Error during timesheet description edit:', err);
          },
        });
    }
  }

  public handleWeDateChange(): void {
    this.datahub.updateQueryParams();
  }

  public addNewTimesheet(): void {
    let contracts: ContractHandler[];
    if (this.isStormOrPrivate(this.currentCondition)) {
      contracts = this.contracts.filter(
        (x) => x.getConditionGrp() === this.currentCondition && x.getCustomerGroup() === this.currentCustomerGrp,
      );
    } else {
      contracts = this.contracts.filter(
        (x) =>
          x.getConditionGrp() === this.currentCondition &&
          x.getCustomerGroup() === this.currentCustomerGrp &&
          x.getCostCenter() === this.currentCostcenter,
      );
    }

    if (contracts.length === 1) {
      if (contracts[0] != null) this.createTimesheetFromContract(contracts[0]);
    } else {
      const data = {
        handler: this.handler,
        contracts,
        allowSearching: false,
        headerText: 'Choosing a contract will create an empty timesheet on the current weekending date',
      };

      this.modalSvc
        .launchResponsiveModal('contract-select', data)
        .afterClosed()
        .pipe(filter((contract: ContractHandler | undefined) => !!contract))
        .subscribe({
          next: (contract: ContractHandler) => {
            this.createTimesheetFromContract(contract);
          },
          error: (err) => {
            console.error('Error during contract selection:', err);
          },
        });
    }
  }

  private createTimesheetFromContract(contract: ContractHandler): void {
    const currentTabsTimesheets = this.timesheets.filter((timesheet) => timesheet.shouldShow);
    this.modalSvc
      .launchResponsiveModal(
        'timesheet-create-or-edit',
        {
          selectedCostCenterCondGrpTabs: {
            costCenter: this.currentCostcenter,
            custGroup: this.currentCustomerGrp,
          },
          headerConfigs: this.config,
          allExtDocuments: currentTabsTimesheets,
        },
        { autoFocus: 'first-tabbable' },
      )
      .afterClosed()
      .pipe(
        filter((x) => x), // remove empty, happens when modal is closed without saving
        switchMap((result: IModalContentCreateOrEditTimesheetResult) => {
          // disable the button and show a loader to indicate we are working
          this.shouldDisableAddTimesheet = true;
          this.isCreatingTimesheet = true;

          return this.timesheetHelperSvc.create$(contract, result.description, result.headers).pipe(
            map((documentResponse): null => {
              const baby = new ExtDocument(documentResponse, this.datahub, false);
              baby.modifyConfig(this.config);
              this.timesheets.push(baby);

              // re-enable the button and hide the loader
              this.shouldDisableAddTimesheet = false;
              this.isCreatingTimesheet = false;

              this.handleTabChange(this.customerGrpsInSearch[this.currentCustomerGrpIndex]);
              return null;
            }),
            catchError(() => {
              this.messageService.error('There was a problem creating this timesheet, please try again later.');
              // re-enable the button and hide the loader
              this.shouldDisableAddTimesheet = false;
              this.isCreatingTimesheet = false;
              return of(null);
            }),
          );
        }),
      )
      .subscribe({
        next: () => {
          const tabToChangeTo = this.customerGrpsInSearch[this.currentCustomerGrpIndex];
          if (tabToChangeTo != null) this.handleTabChange(tabToChangeTo);
        },
        error: () => {
          this.messageService.error('There was a problem creating this timesheet, please try again later.');
          //re-enable the button and hide the loader
          this.shouldDisableAddTimesheet = false;
          this.isCreatingTimesheet = false;
        },
      });
  }

  private isStormOrPrivate(condition: string): boolean {
    return (
      condition === conditionGroups.CONDITION_GROUP_SPECIAL_ID_PRIVATE ||
      condition === conditionGroups.CONDITION_GROUP_SPECIAL_ID_STORM ||
      condition === conditionGroups.CONDITION_GROUP_SPECIAL_ID_WOLF_STORM
    );
  }

  /**@description Take in an array of contracts, and return the array with no duplicates */
  private getUniqueContracts(arr: ContractHandler[]): ContractHandler[] {
    return [...new Map(arr.map((entry) => [entry.value.ContractId, entry])).values()];
  }

  /**@description Take in an array of timesheet data, and return the array with no duplicates */
  private getUniqueTimesheet(arr: ICostCenterConditionDisplay[]): ICostCenterConditionDisplay[] {
    const props: ReadonlyArray<keyof ICostCenterConditionDisplay> = ['condGroup', 'costcenter']; //we combine these properties to determine uniqueness
    return [...new Map(arr.map((entry) => [props.map((k) => entry[k]).join('|'), entry])).values()];
  }
}
